import { useCallback } from 'react'

import * as Sentry from '@sentry/react'

import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'

type RunningQuoteApprovalAutomationExecution =
  | { id: string; createTime: string }
  | undefined

type UseLoadRunningQuoteApprovalAutomationExecutionId = () => (
  quoteId: string | undefined
) => Promise<RunningQuoteApprovalAutomationExecution>

/**
 * Returns an automation execution by quote ID
 *
 * Returns undefined if there is no running automation execution for this quote,
 * with the relevant triggerType and quoteId.
 *
 * If the value returned by this loader is defined, this can be used to drive the UI like
 * disabling the editor if a quote is pending approval, or to cancel the requested approval
 */

export const useLoadRunningQuoteApprovalAutomationExecutionId: UseLoadRunningQuoteApprovalAutomationExecutionId =
  () => {
    const load = useCallback(async (quoteId: string | undefined) => {
      if (!quoteId) return undefined

      const resAutomationExecutions =
        await dashboardv99990101Client.getAutomationExecutions({
          triggerType: 'QUOTE_REQUIRES_APPROVAL',
          status: 'RUNNING',
          quoteId
        })

      if (resAutomationExecutions.error || !resAutomationExecutions.data) {
        /**
         * This is supplementary data used only to drive optional UI (e.g.
         * disabling the editor while a quote is pending approval). A failure to
         * load it must not crash the entire quote editor.
         *
         * Previously this threw, which rejected the `Promise.all` in
         * `useLoadQuoteEditor` and surfaced as an unhandled rejection (Sentry
         * DASHBOARD-WEB-2AD). Instead, capture the failure for observability
         * and degrade gracefully by treating the quote as having no running
         * approval execution — consistent with this loader's documented
         * contract of returning `undefined` when there is none.
         *
         * Disabling the editor on the client is only a UX nicety: the API
         * enforces this independently. `UpdateQuoteUseCase` in
         * sequence-platform-api rejects a direct quote update with a validation
         * error while a RUNNING QUOTE_REQUIRES_APPROVAL automation execution
         * exists, so degrading gracefully here cannot let an invalid edit be
         * saved.
         */
        Sentry.captureException(
          new Error('Unable to get automation executions for this quote'),
          { extra: { quoteId } }
        )
        return undefined
      }

      const automationExecution = resAutomationExecutions.data.items.at(0)

      if (!automationExecution) return undefined

      return {
        id: automationExecution.id,
        createTime: automationExecution.createTime
      }
    }, [])

    return load
  }
